import * as authService from './authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

const BASE = '/api/v1/lucky-wheel'

export type LuckyWheelPaymentType = 'cash' | 'points'

export interface LuckyWheelMode {
  key: string
  label: string
  costPoints: number
  costCash: number
}

export interface LuckyWheelSegment {
  id: string
  label: string
  kind: 'prize' | 'empty' | string
  currency?: string
  amount: number
  weight?: number
  color: string
}

export interface LuckyWheelTicket {
  ticketId: string
  game: string
  paymentType: string
  modeKey?: string
  remaining: number
  expiresAt?: string | null
}

export type LuckyWheelMySpin = {
  spinId: string
  modeKey: string
  paymentType: LuckyWheelPaymentType | string
  paidWith: string
  costAmount: number
  kind: 'prize' | 'empty' | string
  segmentLabel: string
  prizeAmount: number
  prizeCurrency: string
  createdAt: string
}

export type LuckyWheelSpinResult = {
  spinId: string
  modeKey: string
  paymentType: LuckyWheelPaymentType
  paidWith: string
  costAmount: number
  segmentIndex: number
  segmentId: string
  segmentLabel: string
  kind: 'prize' | 'empty' | string
  prizeAmount: number
  prizeCurrency: string
  segments: Array<{
    index: number
    id: string
    label: string
    kind: string
    amount: number
    color: string
  }>
}

function headers() {
  const token = authService.getToken()
  const tenantId = getRuntimeTenantIdSync()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message || `HTTP ${response.status}`,
    ) as Error & { code?: string; status?: number }
    err.code = (data as { code?: string })?.code
    err.status = response.status
    throw err
  }
  return data as T
}

export async function fetchLuckyWheelPlayConfig() {
  const response = await fetch(withApiBaseUrl(`${BASE}/play-config`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: {
      userEntryVisible: boolean
      spinDurationMs?: number
      modes: LuckyWheelMode[]
      baseCostPoints?: number
      baseCostCash?: number
      segmentsPoints: LuckyWheelSegment[]
      segmentsCash: LuckyWheelSegment[]
      tickets: LuckyWheelTicket[]
    }
  }>(response)
}

export async function postLuckyWheelSpin(body: {
  modeKey: string
  paymentType: LuckyWheelPaymentType
  useTicket?: boolean
  ticketId?: string
}) {
  const response = await fetch(withApiBaseUrl(`${BASE}/spins`), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  })
  return parseJson<{ success: boolean; data: LuckyWheelSpinResult }>(response)
}

export async function fetchMyLuckyWheelSpins(params?: { page?: number; limit?: number }) {
  const page = params?.page || 1
  const limit = params?.limit || 20
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) })
  const response = await fetch(withApiBaseUrl(`${BASE}/spins/my?${qs}`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: LuckyWheelMySpin[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }>(response)
}

export async function fetchMyLuckyWheelTickets(game?: string) {
  const qs = new URLSearchParams()
  if (game) qs.set('game', game)
  const suffix = qs.toString() ? `?${qs}` : ''
  const response = await fetch(withApiBaseUrl(`${BASE}/tickets/my${suffix}`), {
    headers: headers(),
  })
  return parseJson<{ success: boolean; data: LuckyWheelTicket[] }>(response)
}
